import * as React from 'react';
import {
  Box,
  Flex,
  Image,
  Stack,
  Text,
  useMediaQuery,
  VStack,
  Menu,
  MenuButton,
  Button,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import visitLibrary from 'assets/model-not-found.svg';
import Container from 'components/Container';
import { Model } from 'models/response/model.interface';
import {
  AddModel,
  getDuplicateModels,
  getModels,
  GetUserModels,
  UpdateModel,
} from 'redux/ModelsSlice';
import FullScreenLoader from 'components/FullScreenLoader';
import ArrorDropdownIconComponent from 'components/Icons/ArrowDropdownIcon';
import AddIconComponent from 'components/Icons/AddIcon';
import CreateModelModal from 'components/Modals/CreateModelModal';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { getUserSubscriptionLimits, SetModelLimit } from 'redux/UserSlice';

import PageTitles from './PageTitle';
import SearchInModels from './ModelsSearch';
import ModelsViewMode from './ModelsViewMode';
import DesktopModel from './DesktopModels';

const DesktopModelsLayout = () => {
  useDocumentTitle('');
  const dispatch: any = useDispatch();

  const [isMobile] = useMediaQuery('(max-width: 850px)');
  const carouselReference = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [addModel, setAddModel] = useState({ type: '', status: false, modelData: {} });
  const [loader, setLoader] = useState(true);

  const models = useSelector(getModels);
  const duplicateModels = useSelector(getDuplicateModels);
  const permissionData = useSelector(getUserSubscriptionLimits);

  useEffect(() => {
    setLoader(true);
    const getUserModel = async () => {
      await dispatch(GetUserModels({ loader: true }));
    };
    getUserModel();
  }, []);

  const duplicateModel = useMemo(() => {
    const filteredModels = models?.filter((model: any) =>
      model?.name?.toLowerCase().includes(searchValue.toLowerCase()),
    );
    setLoader(false);
    return duplicateModels.concat(filteredModels);
  }, [models, searchValue, duplicateModels]);

  const onSearchValue = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const onToggleViewMode = (newMode: string) => setViewMode(newMode);

  const handleAddModel = (form: any, shouldUpdate: boolean, modalData: any) => {
    setAddModel({ modelData: {}, type: '', status: false });
    if (!shouldUpdate) {
      dispatch(
        AddModel({
          data: form,
          toaster: { successMessage: 'Added successfully', errorMessage: true },
        }),
      );
    } else {
      dispatch(UpdateModel({ modelId: modalData?.id, data: form, toaster: true }));
    }
  };

  const addModelForm = () => {
    if (
      permissionData?.subscription_features_limit?.No_of_models &&
      !(permissionData?.subscription_features_limit?.No_of_models > models?.length)
    ) {
      dispatch(SetModelLimit(true));
    } else {
      setAddModel({ status: true, type: 'add', modelData: {} });
    }
  };

  const addLibraryModel = () => {
    if (
      permissionData?.subscription_features_limit?.No_of_models &&
      !(permissionData?.subscription_features_limit?.No_of_models > models?.length)
    ) {
      dispatch(SetModelLimit(true));
    } else {
      window.open('https://www.blox.so/models', '_blank');
    }
  };

  if (loader) return <FullScreenLoader />;

  return (
    <>
      <Container
        wrapperProperties={{
          px: '2.5vw',
          borderTopWidth: '1',
          borderColor: 'grey.900',
          bg: 'white.900',
          overflowX: 'hidden',
        }}
      >
        <Flex direction='column' py='7.5' h='100%' overflow={'hidden'}>
          <PageTitles />
          <Flex position='relative' mb='auto' w='100%' mt='10'>
            <VStack w={'100%'} spacing='4' align='flex-start'>
              <Flex
                width={'100%'}
                flexDirection={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
              >
                <Flex w='inherit' whiteSpace={isMobile ? 'nowrap' : 'initial'}>
                  <Text
                    size='home_section_title'
                    variant='secondary'
                    alignSelf={'center'}
                    whiteSpace={'nowrap'}
                  >
                    Your saved Models :
                  </Text>
                  <SearchInModels searchValue={searchValue} onChange={onSearchValue} />
                </Flex>
                <Flex>
                  {duplicateModel?.length > 0 && (
                    <ModelsViewMode viewMode={viewMode} onChange={onToggleViewMode} />
                  )}
                  <Menu closeOnSelect={true} variant='primary'>
                    <MenuButton
                      ml={8}
                      mr={2}
                      backgroundColor={'#5A58AB'}
                      padding={'.5rem 1rem'}
                      as={Button}
                      border={'none'}
                      rightIcon={
                        <ArrorDropdownIconComponent
                          style={{ margin: '0 .5rem' }}
                          height={12}
                          width={12}
                          color='white'
                        />
                      }
                      leftIcon={
                        <AddIconComponent
                          style={{ margin: '0 .5rem' }}
                          color='#62B5BA'
                          height={18}
                          width={18}
                        />
                      }
                      fontSize={'md'}
                      fontWeight={'400'}
                      width={'fit-content'}
                      height={'40px'}
                    >
                      Create
                    </MenuButton>
                    <MenuList zIndex={4}>
                      <MenuItem onClick={() => addModelForm()}>New blank model</MenuItem>
                      <MenuItem onClick={() => addLibraryModel()}>New model from library</MenuItem>
                    </MenuList>
                  </Menu>
                </Flex>
              </Flex>
              <Box
                className='scroll'
                height={'calc(100vh - 250px)'}
                overflow={'auto'}
                ref={carouselReference}
                w={'100%'}
              >
                {duplicateModel?.length > 0 ? (
                  <Stack
                    flexDirection={'row'}
                    gap={'4'}
                    flexWrap={'wrap'}
                    alignItems={'flex-start'}
                    position='relative'
                  >
                    {duplicateModel?.map((model: Model, index: number) => (
                      <React.Fragment key={index}>
                        <DesktopModel
                          key={index}
                          model={model}
                          isListMode={viewMode === 'list'}
                          handleProperties={(data: any) => {
                            setAddModel({ type: 'edit', status: true, modelData: data });
                          }}
                        />
                      </React.Fragment>
                    ))}
                  </Stack>
                ) : models?.length === 0 ? (
                  <Flex justifyContent={'end'}>
                    <Image height={'5rem'} mt={15} src={visitLibrary} />
                  </Flex>
                ) : (
                  <Text size='home_page_subtitle' variant='grey'>
                    No blocks found with that name
                  </Text>
                )}
              </Box>
            </VStack>
          </Flex>
        </Flex>
      </Container>

      <CreateModelModal
        modalData={addModel.modelData}
        modalType={addModel.type}
        isOpen={addModel.status}
        onClose={() => setAddModel({ status: false, type: '', modelData: {} })}
        onSave={handleAddModel}
      />
    </>
  );
};

export default DesktopModelsLayout;
